import { ChevronRight, Trophy } from "lucide-react";
import { Link } from "react-router";

import { AttemptOutcome } from "@/components/attempt-outcome";
import { MetricHead } from "@/components/metric-head";
import { ScoreCell } from "@/components/score-cell";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Challenge, SimSession } from "@/lib/api";
import {
  formatDegrees,
  formatError,
  formatRelative,
  formatSeconds,
  formatTimestamp,
} from "@/lib/format";
import { attemptHref } from "@/lib/links";
import { bestRun, formatSpl, sessionRun } from "@/lib/scoring";
import { cn } from "@/lib/utils";

/**
 * A student's sessions at a server-simulated challenge, one row each, every row a link.
 *
 * The counterpart of `SubmissionsTable` for the mode where the session is the unit: a run
 * that was aborted or timed out has no submission, but it spent an attempt, and a student
 * looking for "the one that disconnected on Tuesday" has to be able to find it. Numbered
 * as attempts, oldest = 1, to match the detail page's "Attempt N"; listed newest first.
 */
export function AttemptsTable({
  challenge,
  sessions,
  className,
}: {
  challenge: Challenge;
  sessions: readonly SimSession[];
  className?: string;
}) {
  const oldestFirst = [...sessions].sort((a, b) => a.started_at - b.started_at);
  const attemptNumber = new Map(oldestFirst.map((s, i) => [s.id, i + 1]));
  const spec = challenge.rubric_spec;
  const best = bestRun(sessions.map(sessionRun))?.session;
  // The metric columns are the rubric's: distance and steps always, since they rank,
  // and heading and SPL when the rubric grades them. One line per cell, so a row that
  // was aborted is the same height as one that scored. Distance and heading are blank
  // for a miss: the server does not say how near one came.
  const showHeading = spec.variables.includes("heading");
  const showSpl = spec.variables.includes("spl");

  return (
    <div className={cn("overflow-hidden rounded-lg border", className)}>
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40 hover:bg-muted/40">
            <TableHead className="w-12 text-right">#</TableHead>
            <TableHead>Started</TableHead>
            <MetricHead metric="result" />
            <MetricHead metric="score" className="text-right" />
            <MetricHead metric="steps" className="text-right" />
            {showSpl ? <MetricHead metric="spl" className="hidden text-right md:table-cell" /> : null}
            <MetricHead metric="distance" className="hidden text-right sm:table-cell" />
            {showHeading ? (
              <MetricHead metric="heading" className="hidden text-right md:table-cell" />
            ) : null}
            <MetricHead metric="time" className="hidden text-right md:table-cell" />
            <TableHead className="w-8" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...oldestFirst].reverse().map((row) => {
            const isBest = best?.id === row.id;
            const live = row.status === "running";
            return (
              <TableRow
                key={row.id}
                className={cn(
                  "group/row has-[a:focus-visible]:bg-muted/50 relative cursor-pointer",
                  live && "bg-primary/5",
                )}
              >
                <TableCell className="text-muted-foreground text-right tabular-nums">
                  <Link
                    to={attemptHref(challenge.id, row.id)}
                    className="after:absolute after:inset-0 after:content-[''] focus-visible:outline-none"
                    aria-label={`Attempt ${attemptNumber.get(row.id)}, ${formatTimestamp(row.started_at)}`}
                  >
                    {attemptNumber.get(row.id)}
                  </Link>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <span className="sm:hidden">{formatRelative(row.started_at)}</span>
                  <span className="hidden sm:inline">{formatTimestamp(row.started_at)}</span>
                  <span className="text-muted-foreground hidden text-xs lg:inline">
                    {" · "}
                    {formatRelative(row.started_at)}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="flex items-center gap-1.5">
                    <AttemptOutcome session={row} />
                    {isBest && sessions.length > 1 ? (
                      <Trophy className="text-primary size-3.5" aria-label="Your best" />
                    ) : null}
                    {!live && row.status !== "scored" && row.end_reason ? (
                      <span className="text-muted-foreground hidden truncate text-xs md:inline">
                        {row.end_reason}
                      </span>
                    ) : null}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <ScoreCell points={row.result?.score.points ?? null} max={spec.max_points} />
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {row.steps_used.toLocaleString()}
                </TableCell>
                {showSpl ? (
                  <TableCell className="text-muted-foreground hidden text-right tabular-nums md:table-cell">
                    {formatSpl(row.result?.spl ?? null)}
                  </TableCell>
                ) : null}
                <TableCell
                  className={cn(
                    "hidden text-right tabular-nums sm:table-cell",
                    !row.result?.arrival && "text-muted-foreground",
                  )}
                >
                  {formatError(row.result?.arrival?.distance_m ?? null)}
                </TableCell>
                {showHeading ? (
                  <TableCell className="text-muted-foreground hidden text-right tabular-nums md:table-cell">
                    {formatDegrees(row.result?.arrival?.heading_deg ?? null)}
                  </TableCell>
                ) : null}
                <TableCell className="text-muted-foreground hidden text-right tabular-nums md:table-cell">
                  {formatSeconds(row.timing.wall_ms === null ? null : row.timing.wall_ms / 1000)}
                </TableCell>
                <TableCell className="text-muted-foreground pr-3 text-right">
                  <ChevronRight
                    className="group-hover/row:text-foreground inline size-4 transition-colors"
                    aria-hidden
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
